import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { LazyLoadEvent } from 'primeng/api';
import { AppBreadcrumbService } from 'src/app/app.breadcrumb.service';
import {
    getWhatsappConversationDerivedStatusDetail,
    getWhatsappConversationDirectionIcon,
    getWhatsappConversationDirectionLabel,
    getWhatsappConversationUiStatus,
    getWhatsappConversationWindowState,
    WHATSAPP_CONVERSATION_ACCESS,
    WHATSAPP_CONVERSATION_STATUS,
    WHATSAPP_CONVERSATION_UI_STATUS_OPTIONS,
    WhatsappConversationUiStatusCode,
    WhatsappConversationSummary,
} from 'src/app/models/notificacion/whatsapp_conversation.model';
import { EmpresaService } from 'src/app/services/configuracion/empresa.service';
import { WhatsappConversationService } from 'src/app/services/notificacion/whatsapp_conversation.service';

@Component({
    selector: 'app-whatsapp-conversation-lista',
    templateUrl: './whatsapp-conversation-lista.component.html',
    styleUrls: ['./whatsapp-conversation-lista.component.scss']
})
export class WhatsappConversationListaComponent implements OnInit {

    conversations: WhatsappConversationSummary[] = [];
    totalRecords = 0;
    loading = false;
    empresaSuggestions: Record<string, any>[] = [];

    page = 0;
    size = 20;
    readonly empresaSuggestionSize = 20;
    readonly rowsPerPageOptions = [10, 20, 50];
    readonly statusOptions = [{ valor: null, nombre: 'Todos' }, ...WHATSAPP_CONVERSATION_UI_STATUS_OPTIONS];
    readonly filterForm = this.formBuilder.group({
        id_empresa: [null],
        empresa_lookup: [null],
        search: [''],
        conversation_id: [null],
        conversation_start_from: [null as Date | null],
        conversation_start_to: [null as Date | null],
        ui_status: [null as WhatsappConversationUiStatusCode | null],
        only_unread: [false],
        only_unassigned: [false],
    });

    constructor(
        private formBuilder: FormBuilder,
        private router: Router,
        private messageService: MessageService,
        private breadcrumbService: AppBreadcrumbService,
        private empresaService: EmpresaService,
        private service: WhatsappConversationService,
    ) {
        this.breadcrumbService.setItems([
            { label: 'Notificacion' },
            { label: 'Inbox WhatsApp' },
        ]);
    }

    get currentUser(): Record<string, any> {
        return this.service.seguridadService.getCurrentUser() || {};
    }

    get isSuperUser(): boolean {
        return this.service.seguridadService.esSuperusuario();
    }

    ngOnInit(): void {
        if (!this.service.seguridadService.puedeVerSync(
            WHATSAPP_CONVERSATION_ACCESS.CODIGO_MODULO,
            WHATSAPP_CONVERSATION_ACCESS.CODIGO_REGISTRO,
        )) {
            this.messageService.add({ severity: 'warn', summary: 'Acceso denegado', detail: 'Tu grupo de acceso no puede ver Inbox WhatsApp.' });
            this.router.navigate(['pages', 'access']);
            return;
        }

        this.setEmpresa(this.getDefaultEmpresa());
        this.loadConversations();
    }

    async searchEmpresas(event: Record<string, any>): Promise<void> {
        try {
            const query = String(event?.query || '').trim();
            const selectedEmpresa = this.filterForm.value.empresa_lookup;
            const hasSelectedEmpresa = Boolean(selectedEmpresa && typeof selectedEmpresa === 'object' && selectedEmpresa.id);

            if (!query) {
                if (!hasSelectedEmpresa) {
                    this.filterForm.patchValue({ id_empresa: null }, { emitEvent: false });
                }
                this.empresaSuggestions = await this.fetchEmpresaSuggestions();
                return;
            }

            if (!hasSelectedEmpresa || selectedEmpresa.nombre !== query) {
                this.filterForm.patchValue({ id_empresa: null }, { emitEvent: false });
            }

            this.empresaSuggestions = await this.fetchEmpresaSuggestions(query);
        } catch (error: any) {
            this.empresaSuggestions = [];
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.message || 'No se pudieron consultar las empresas' });
        }
    }

    onEmpresaSelect(empresa: Record<string, any> | null): void {
        this.setEmpresa(empresa);
    }

    async loadConversations(event?: LazyLoadEvent): Promise<void> {
        try {
            this.loading = true;

            if (event) {
                this.size = Number(event.rows || this.size);
                this.page = Math.floor(Number(event.first || 0) / this.size);
            }

            const selectedCompanyId = this.getSelectedCompanyId();
            if (!selectedCompanyId) {
                this.conversations = [];
                this.totalRecords = 0;
                if (this.isSuperUser) {
                    this.messageService.add({ severity: 'warn', summary: 'Empresa requerida', detail: 'Seleccione una empresa para consultar el inbox.' });
                }
                return;
            }

            const response = await this.service.listConversations({
                idEmpresa: selectedCompanyId,
                page: this.page,
                size: this.size,
                search: String(this.filterForm.value.search || '').trim(),
                conversationId: Number(this.filterForm.value.conversation_id || 0) || null,
                conversationStartFrom: this.filterForm.value.conversation_start_from,
                conversationStartTo: this.filterForm.value.conversation_start_to,
                uiStatus: this.filterForm.value.ui_status,
                onlyUnread: Boolean(this.filterForm.value.only_unread),
                onlyUnassigned: Boolean(this.filterForm.value.only_unassigned),
            });

            if (!response.ok) {
                this.messageService.add({ severity: 'error', summary: response.msg, detail: response.resp });
                this.conversations = [];
                this.totalRecords = 0;
                return;
            }

            this.conversations = this.service.extractConversationList(response);
            this.totalRecords = Number(response.resp?.count || 0);
        } catch (error: any) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.message || 'No se pudo cargar el inbox WhatsApp' });
        } finally {
            this.loading = false;
        }
    }

    async applyFilters(): Promise<void> {
        this.page = 0;
        await this.loadConversations();
    }

    async clearFilters(): Promise<void> {
        this.filterForm.reset({
            id_empresa: this.getDefaultEmpresa()?.id || null,
            empresa_lookup: this.mapEmpresaOption(this.getDefaultEmpresa()),
            search: '',
            conversation_id: null,
            conversation_start_from: null,
            conversation_start_to: null,
            ui_status: null,
            only_unread: false,
            only_unassigned: false,
        });
        this.page = 0;
        await this.loadConversations();
    }

    openConversation(conversation: WhatsappConversationSummary): void {
        this.router.navigate(['pages', 'notificacion', 'whatsapp-inbox', conversation.id], {
            queryParams: { idEmpresa: conversation.id_empresa },
        });
    }

    getStatusLabel(conversation: WhatsappConversationSummary): string {
        return getWhatsappConversationUiStatus(conversation).label;
    }

    getStatusSeverity(conversation: WhatsappConversationSummary): string {
        return getWhatsappConversationUiStatus(conversation).severity;
    }

    getStatusDetail(conversation: WhatsappConversationSummary): string | null {
        return getWhatsappConversationDerivedStatusDetail(conversation);
    }

    /**
     * Un flujo pausado por ventana cerrada NO cambia `status_code` — la
     * conversación sigue viéndose "Pendiente"/"En Atención" por las mismas
     * reglas de siempre (S5f.3: extiende el listado existente, no decide
     * nada nuevo). Esto solo hace visible, con el dato que YA calcula el
     * backend (`is_outside_window`), que además está fuera de ventana — para
     * que un agente entienda por qué el flujo no pudo seguir enviando solo.
     */
    showWindowClosedHint(conversation: WhatsappConversationSummary): boolean {
        const activeStatusCodes: number[] = [WHATSAPP_CONVERSATION_STATUS.PENDIENTE, WHATSAPP_CONVERSATION_STATUS.EN_ATENCION];
        if (!activeStatusCodes.includes(Number(conversation.status_code))) {
            return false;
        }

        return getWhatsappConversationWindowState(conversation).isOutsideWindow === true;
    }

    getDirectionLabel(directionCode: number | null): string {
        return getWhatsappConversationDirectionLabel(directionCode);
    }

    getDirectionIcon(directionCode: number | null): string {
        return getWhatsappConversationDirectionIcon(directionCode);
    }

    private getSelectedCompanyId(): number {
        if (!this.isSuperUser) {
            return Number(this.currentUser?.id_empresa || 0);
        }

        const selectedEmpresa = this.mapEmpresaOption(this.filterForm.value.empresa_lookup || null);
        const selectedCompanyId = Number(this.filterForm.value.id_empresa || selectedEmpresa?.id || 0);

        if (selectedCompanyId && Number(this.filterForm.value.id_empresa || 0) !== selectedCompanyId) {
            this.filterForm.patchValue({ id_empresa: selectedCompanyId }, { emitEvent: false });
        }

        return selectedCompanyId;
    }

    private getDefaultEmpresa(): Record<string, any> | null {
        const idEmpresa = Number(this.currentUser?.id_empresa || 0);
        if (!idEmpresa) {
            return null;
        }

        return {
            id: idEmpresa,
            nombre: this.currentUser?.nombre_empresa || '',
        };
    }

    private setEmpresa(empresa: Record<string, any> | null): void {
        const empresaOption = this.mapEmpresaOption(empresa);

        this.filterForm.patchValue({
            id_empresa: empresaOption?.id || null,
            empresa_lookup: empresaOption,
        }, { emitEvent: false });
    }

    private async fetchEmpresaSuggestions(query?: string): Promise<Record<string, any>[]> {
        const filtros = [];
        const normalizedQuery = String(query || '').trim();

        if (normalizedQuery) {
            filtros.push(
                /^\d+$/.test(normalizedQuery)
                    ? { field: 'id', value: Number(normalizedQuery), type: 'id' }
                    : { field: 'nombre', value: normalizedQuery, type: 'text' },
            );
        }

        const response = await this.empresaService.getAll(this.empresaSuggestionSize, { filters: filtros });
        if (!response.ok) {
            throw new Error(response.msg || 'No se pudieron consultar las empresas');
        }

        return (response.resp?.rows || []).map((empresa: Record<string, any>) => this.mapEmpresaOption(empresa));
    }

    private mapEmpresaOption(empresa: Record<string, any> | null): Record<string, any> | null {
        if (!empresa) {
            return null;
        }

        const id = Number(empresa.id);
        const nombre = empresa.nombre || `Empresa ${empresa.id}`;

        if (empresa.id === id && empresa.nombre === nombre) {
            return empresa;
        }

        return {
            ...empresa,
            id,
            nombre,
        };
    }
}
